// src/components/Sidebar.jsx
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { MdDashboard, MdPeople, MdSettings, MdHelpOutline, MdQueryStats } from 'react-icons/md';
import { Activity } from 'lucide-react';
import { useCampaignAccess } from '../contexts/CampaignAccessContext';
import { useSessionStorage } from '../contexts/SessionStorageContext';
import { useTheme } from '../contexts/ThemeContext';
import { isUsageAdminUser } from '../utils/adminAccess';
import { usePollerHealth } from '../hooks/usePollerHealth';
import SiteSiftLogo from './brand/SiteSiftLogo';
import '../styles/SideBar.css';

export default function SideBar({ clientCount = 0, recentlyCreatedClient = false, isLoadingClients = false }) {
  const location = useLocation();
  const campaignAccess = useCampaignAccess();
  const { sessionData } = useSessionStorage();
  const { theme } = useTheme();
  const logoTone = theme === 'light' ? 'dark' : 'light';
  const showUsage = isUsageAdminUser(campaignAccess);
  const { health: pollerHealth } = usePollerHealth(showUsage);

  // Organization name loading is now handled by SessionStorageContext
  // No need for direct Firestore fetch here

  const primaryLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: <MdDashboard size={20}/> },
    { to: '/clients',   label: 'Manage Clients', icon: <MdPeople    size={20}/> },
  ];
  const adminLinks = showUsage ? [
    { to: '/usage', label: 'Usage', icon: <MdQueryStats size={20}/> },
    { to: '/operations', label: 'Operations', icon: <Activity size={20}/> },
  ] : [];
  const supportLinks = [
    { to: '/settings',  label: 'Settings',       icon: <MdSettings  size={20}/> },
    { to: '/help',      label: 'Help',           icon: <MdHelpOutline size={20}/> },
  ];
  const isSecondaryPage = ['/settings', '/help', '/usage', '/operations'].some(path => location.pathname.startsWith(path));

  const renderLink = ({ to, label, icon }) => {
    const shouldPulseClients = !isLoadingClients && clientCount === 0 && to === '/clients' && location.pathname !== '/clients' && !isSecondaryPage;
    const shouldPulseDashboard = recentlyCreatedClient && to === '/dashboard' && location.pathname !== '/dashboard';
    const shouldPulse = shouldPulseClients || shouldPulseDashboard;

    return (
      <NavLink
        key={to}
        to={to}
        className={({ isActive }) => {
          let className = isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link';
          if (shouldPulse) className += ' sidebar__link--pulse';
          return className;
        }}
      >
        <span className="sidebar__icon">{icon}</span>
        <span className="sidebar__label">{label}</span>
      </NavLink>
    );
  };

  return (
    <aside className="sidebar">
      <div className="sidebar__logo">
        <div className="sidebar__brand-mark">
          <SiteSiftLogo height={58} tone={logoTone} />
        </div>
        {sessionData.isLoadingProfile ? (
          <div className="sidebar__logo-skeleton"></div>
        ) : (
          <div className="sidebar__logo-content">
            {sessionData.organizationName || '\u00A0'}
          </div>
        )}
      </div>
      <nav className={showUsage ? 'sidebar__nav sidebar__nav--admin' : 'sidebar__nav'}>
        <div className="sidebar__nav-group" aria-label="Primary navigation">
          {primaryLinks.map(renderLink)}
        </div>
        {showUsage && (
          <>
            <div className="sidebar__nav-separator" role="separator" />
            <div className="sidebar__nav-group sidebar__nav-group--admin" aria-label="Admin navigation">
              <span className="sidebar__section-label">Admin</span>
              {adminLinks.map(renderLink)}
              {pollerHealth?.alarm && (
                <NavLink to="/operations" className="sidebar__alarm" data-testid="sidebar-poller-alarm" title={pollerHealth.detail}>
                  <span className="sidebar__alarm-dot" aria-hidden="true" />
                  <span>{pollerHealth.tone === 'down' ? 'Poller down' : pollerHealth.tone === 'stale' ? 'Poller stale' : 'Poller unknown'}</span>
                </NavLink>
              )}
            </div>
          </>
        )}
        <div className="sidebar__nav-separator" role="separator" />
        <div className="sidebar__nav-group" aria-label="Support navigation">
          {supportLinks.map(renderLink)}
        </div>
      </nav>
    </aside>
  );
}
